/**
 * Nombres de médicos y números de colegiado (dirección, 2026-09-08, punto 7).
 *
 * Mismo criterio que backend/services/medicos.py: todo médico va como
 * «Dr./Dra. Nombre Apellido1 Apellido2», bien escrito (tildes y mayúsculas),
 * con su número de colegiado (o «pendiente» mientras no lo tengamos). El
 * backend vuelve a validar (422); aquí se avisa antes de enviar.
 */

export const TIPOS_MEDICO = ['cirujano', 'anestesista'];
// Instrumentistas y circulantes (administración, 2026-09-09, punto 7): NO son
// médicos. Van con «Nombre Apellido1 Apellido2», sin Dr./Dra. ni colegiado.
export const TIPOS_PERSONA = ['instrumentista', 'circulante'];
export const TITULOS = ['Dr.', 'Dra.'];

const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e', 'da', 'do', 'dos', 'das', 'di', 'van', 'von', 'der', 'san', 'santa']);
const RE_PALABRA = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñÇç][A-Za-zÁÉÍÓÚÜÑáéíóúüñÇç'’-]*$/;

export const MENSAJE_FORMATO = 'El nombre debe ir como «Dr./Dra. Nombre Apellido1 Apellido2» (tratamiento, nombre y dos apellidos, con sus tildes y mayúsculas)';
export const MENSAJE_COLEGIADO = 'Indica el número de colegiado (solo cifras, entre 6 y 12)';
export const MENSAJE_FORMATO_PERSONA = 'El nombre debe ir como «Nombre Apellido1 Apellido2» (nombre y dos apellidos, con sus tildes y mayúsculas)';

export function esTipoMedico(tipo) {
  return TIPOS_MEDICO.includes(tipo);
}

export function esTipoPersona(tipo) {
  return TIPOS_PERSONA.includes(tipo);
}

/** Los tipos cuyo alta pide un formato: médicos y personal de quirófano. */
export function esTipoConAlta(tipo) {
  return esTipoMedico(tipo) || esTipoPersona(tipo);
}

function capitalizar(palabra) {
  if (palabra === palabra.toLowerCase() || palabra === palabra.toUpperCase()) {
    return palabra.split(/([-'’])/).map((p) => (p && !'-\'’'.includes(p) ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p)).join('');
  }
  return palabra;
}

/** («Dr.»|«Dra.»|null, palabras restantes) a partir de lo tecleado. */
function partirNombre(nombre) {
  const palabras = (nombre || '').trim().split(/\s+/).filter(Boolean);
  if (!palabras.length) return { titulo: null, resto: [] };
  const primera = palabras[0].replace(/\.$/, '').toLowerCase();
  const titulo = primera === 'dr' ? 'Dr.' : primera === 'dra' ? 'Dra.' : null;
  return titulo ? { titulo, resto: palabras.slice(1) } : { titulo: null, resto: palabras };
}

/** «nombre apellido1 apellido2» limpio, o null si no llega a tres palabras. */
function nombreYDosApellidos(resto) {
  let contenido = 0;
  const limpias = [];
  for (const p of resto) {
    if (!RE_PALABRA.test(p)) return null;
    if (PARTICULAS.has(p.toLowerCase())) {
      limpias.push(p === p.toUpperCase() ? p.toLowerCase() : p);
      continue;
    }
    contenido += 1;
    limpias.push(capitalizar(p));
  }
  if (contenido < 3) return null;
  return limpias.join(' ');
}

/** Nombre en formato oficial o null si no cumple (mensaje en MENSAJE_FORMATO). */
export function normalizarNombreMedico(nombre) {
  const { titulo, resto } = partirNombre(nombre);
  if (!titulo) return null;
  const limpio = nombreYDosApellidos(resto);
  return limpio ? `${titulo} ${limpio}` : null;
}

/**
 * Nombre de quien NO es médico (instrumentistas y circulantes), o null
 * (mensaje en MENSAJE_FORMATO_PERSONA). Sin Dr./Dra.: si lleva tratamiento
 * también se rechaza (administración, 2026-09-09, punto 7).
 */
export function normalizarNombrePersona(nombre) {
  const { titulo, resto } = partirNombre(nombre);
  if (titulo) return null;
  return nombreYDosApellidos(resto);
}

/** Colegiado como cadena de cifras o null si no vale (MENSAJE_COLEGIADO). */
export function normalizarColegiado(valor) {
  const digitos = String(valor || '').replace(/[\s.\-/]/g, '');
  if (!/^\d{6,12}$/.test(digitos)) return null;
  return digitos;
}

/** «Dr.» + nombre + apellidos tecleados por separado → una sola cadena. */
export function componerNombreMedico({ titulo, nombre, apellido1, apellido2 }) {
  return [titulo, nombre, apellido1, apellido2].map((x) => (x || '').trim()).filter(Boolean).join(' ');
}

/** Lo que se enseña junto al nombre en los desplegables. */
export function etiquetaColegiado(colegiado) {
  const c = (colegiado || '').toString().trim();
  return c ? `Col. ${c}` : 'Col. pendiente';
}

const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\./g, ' ').replace(/\s+/g, ' ').trim();

/** Palabras del nombre sin tratamiento ni partículas (para comparar). */
function palabras(nombre) {
  return norm(nombre).split(/\s+/).filter((p) => p && !['dr', 'dra'].includes(p) && !PARTICULAS.has(p));
}

/**
 * La entrada del catálogo que corresponde a un usuario (por su nombre): la
 * misma o, si el usuario aún lleva un nombre corto («Juan»), aquella cuyo
 * nombre contiene todas sus palabras. Null si no hay una sola candidata.
 */
export function entradaDeUsuario(nombreUsuario, entradas) {
  const mias = palabras(nombreUsuario);
  if (!mias.length) return null;
  const exacta = (entradas || []).find((e) => norm(e.nombre) === norm(nombreUsuario));
  if (exacta) return exacta;
  const candidatas = (entradas || []).filter((e) => {
    const suyas = new Set(palabras(e.nombre));
    return mias.every((p) => suyas.has(p));
  });
  return candidatas.length === 1 ? candidatas[0] : null;
}

/**
 * El nombre OFICIAL con el que se enseña un valor escrito en un registro
 * antiguo: la ficha del catálogo cuyo nombre actual —o alguno de los que tuvo
 * antes (`nombres_anteriores`)— coincide con él, sin distinguir tildes ni
 * mayúsculas. Si no es de nadie del catálogo («Externo», el nombre de una clínica), se
 * devuelve tal cual: no se inventa ni se esconde.
 *
 * Administración (2026-09-09, punto 2): así los desplegables de filtro del registro
 * de quirófano dejan de enseñar a la misma persona dos veces, con el nombre de
 * ahora y con el de entonces. Los registros no se tocan (punto 6).
 */
export function canonicoDeCatalogo(tipo, valor, entradas) {
  const v = norm(valor);
  if (!v) return '';
  const ficha = (entradas || []).find((e) => e.tipo === tipo && (
    norm(e.nombre) === v || (e.nombres_anteriores || []).some((n) => norm(n) === v)
  ));
  return ficha ? ficha.nombre : (valor || '').toString().trim();
}
